import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useBusinessProfile } from '../../context/BusinessProfileContext';
import pebbleLogo from '../../assets/images/pebble-logo.png';
import DOMAIN_CONFIG from '../../config/domains';

const SubscriptionSuccess = () => {
  const { user, updateSubscription, checkSubscriptionStatus } = useAuth();
  const { businessProfileData } = useBusinessProfile();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 5;

  const profileBelongsToCurrentUser = user && businessProfileData?.UserID === user.uid;
  const hasCompleteProfile = profileBelongsToCurrentUser &&
    businessProfileData?.fullName?.trim() &&
    businessProfileData?.companyName?.trim();

  useEffect(() => {
    const verifySubscription = async () => {
      if (!user) return;

      try {
        // Check subscription status from Firestore (webhook updates this)
        const result = await checkSubscriptionStatus(user.uid);

        if (result?.hasActiveSubscription) {
          // Update local auth state
          updateSubscription({
            hasActive: true,
            status: result.status || 'active',
            tier: result.plan || 'basic'
          });

          // First-timer: reset onboarding so they see welcome modal
          if (!hasCompleteProfile) {
            try {
              const { doc, updateDoc } = await import('firebase/firestore');
              const { db } = await import('../../firebaseFunctions/config');
              await updateDoc(doc(db, 'users', user.uid), {
                'onboarding.hasSeenWelcome': false,
                isEoSignedUp: true
              });
            } catch (e) {
              console.log('Could not reset onboarding state:', e);
            }
          }

          // Set flag so dashboard shows "Welcome to Pro" banner
          sessionStorage.setItem('showProWelcome', 'true');

          // Redirect based on profile status
          if (hasCompleteProfile) {
            DOMAIN_CONFIG.redirectToApp('/eo/dashboard');
          } else {
            DOMAIN_CONFIG.redirectToApp('/eo/complete-profile');
          }
        } else if (retryCount < maxRetries) {
          // Webhook might not have processed yet, retry after a delay
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 2000);
        } else {
          setError('Subscription verification timed out. Please check your dashboard or contact support.');
          setStatus('error');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError(err.message || 'Failed to verify subscription');
        setStatus('error');
      }
    };

    verifySubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, retryCount, hasCompleteProfile]);

  const handleRetry = () => {
    DOMAIN_CONFIG.redirectToApp('/eo-paywall');
  };

  const handleGoToDashboard = () => {
    DOMAIN_CONFIG.redirectToApp('/eo/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-grey-50 via-white to-primary-25 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <img src={pebbleLogo} alt="Pebble" className="h-8 mx-auto mb-8" />
          
          {status === 'verifying' && (
            <div>
              <h1 className="text-2xl font-bold text-grey-900 mb-2">
                Setting Up Your Account
              </h1>
              <p className="text-grey-600">
                Please wait while we confirm your subscription...
              </p>
            </div>
          )}

          {status === 'error' && (
            <div>
              <div className="w-16 h-16 mx-auto mb-6 bg-red-50 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <h1 className="text-2xl font-bold text-grey-900 mb-2">
                Something Went Wrong
              </h1>
              <p className="text-grey-600 mb-2">
                We couldn't verify your subscription.
              </p>
              {error && (
                <p className="text-sm text-red-600 mb-6">{error}</p>
              )}

              <div className="space-y-3">
                <button
                  onClick={handleRetry}
                  className="w-full py-3 px-6 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all"
                >
                  Try Again
                </button>
                <button
                  onClick={handleGoToDashboard}
                  className="w-full py-3 px-6 bg-grey-100 text-grey-700 rounded-xl font-semibold hover:bg-grey-200 transition-all"
                >
                  Check Dashboard
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-grey-500 mt-6">
          Need help? Contact support@pebbletheapp.com
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
